// React hook owning the desktop floating bug-report pill's persisted
// position + a transient drag preview. Pure math lives in
// `bugReportDockPositionMath.ts`.
//
// Locked behavior (docs/planning/bug_report_placement_v2.md "Desktop path"):
// - Truly free-floating (no edge snap). User can drag the pill anywhere; on
//   release it clamps so the entire pill remains on-screen.
// - Position persisted to localStorage, paired with the viewport size at save
//   time so a different display can reproject proportionally on next load.
// - Default initial position: bottom-right minus 24px padding — visually
//   identical to the legacy fixed pill, so existing users see no change on
//   first load.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  BUG_REPORT_DOCK_POSITION_STORAGE_KEY,
  clampPosition,
  defaultPosition,
  parseStoredPosition,
  scalePosition,
  type BugReportDockPosition,
  type StoredBugReportDockPosition,
} from "./bugReportDockPositionMath";

// Re-export the math surface so existing consumers (and unit tests)
// importing from this file continue to work after the split.
export {
  BUG_REPORT_DOCK_POSITION_STORAGE_KEY,
  clampPosition,
  defaultPosition,
  parseStoredPosition,
  scalePosition,
};
export type { BugReportDockPosition, StoredBugReportDockPosition };

function readStored(): StoredBugReportDockPosition | null {
  if (typeof window === "undefined") return null;
  try {
    return parseStoredPosition(window.localStorage.getItem(BUG_REPORT_DOCK_POSITION_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStored(pos: StoredBugReportDockPosition): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(BUG_REPORT_DOCK_POSITION_STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* quota / private mode — non-fatal */
  }
}

function clearStored(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(BUG_REPORT_DOCK_POSITION_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

export type UseBugReportDockPositionArgs = {
  /** Measured pill width in px (passed by the component once it mounts). */
  elementW: number;
  /** Measured pill height in px. */
  elementH: number;
};

export type UseBugReportDockPositionResult = {
  position: BugReportDockPosition;
  dragPreview: BugReportDockPosition | null;
  isDragging: boolean;
  beginDrag: (x: number, y: number) => void;
  updateDrag: (x: number, y: number) => void;
  endDrag: (x: number, y: number) => void;
  cancelDrag: () => void;
  resetPosition: () => void;
};

export function useBugReportDockPosition({
  elementW,
  elementH,
}: UseBugReportDockPositionArgs): UseBugReportDockPositionResult {
  // Track the drag pointer offset relative to the pill's top-left so the pill
  // doesn't snap its corner to the cursor mid-drag.
  const dragGrabOffsetRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const elementSizeRef = useRef<{ w: number; h: number }>({ w: elementW, h: elementH });
  elementSizeRef.current = { w: elementW, h: elementH };

  const [position, setPosition] = useState<BugReportDockPosition>(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const stored = readStored();
    const raw = stored
      ? scalePosition({ stored, currentVw: vw, currentVh: vh })
      : defaultPosition({ viewportW: vw, viewportH: vh, elementW, elementH });
    return clampPosition({
      x: raw.x,
      y: raw.y,
      viewportW: vw,
      viewportH: vh,
      elementW,
      elementH,
    });
  });
  const [dragPreview, setDragPreview] = useState<BugReportDockPosition | null>(null);

  // Re-clamp on viewport resize so the pill never ends up partially off-screen.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onResize() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { w, h } = elementSizeRef.current;
      setPosition((prev) =>
        clampPosition({ x: prev.x, y: prev.y, viewportW: vw, viewportH: vh, elementW: w, elementH: h }),
      );
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Re-clamp when the pill's measured size changes (e.g. badge appears,
  // making the pill wider). Without this the pill can be pinned slightly
  // past the right edge after a hot-reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setPosition((prev) =>
      clampPosition({
        x: prev.x,
        y: prev.y,
        viewportW: window.innerWidth,
        viewportH: window.innerHeight,
        elementW,
        elementH,
      }),
    );
  }, [elementW, elementH]);

  const beginDrag = useCallback((x: number, y: number) => {
    // Record where the pointer is RELATIVE TO the pill's top-left so dragging
    // doesn't yank the corner to the cursor. The component reads `position`
    // at this moment and passes (x - position.x, y - position.y) via the
    // pointer-down handler; for simplicity here we recompute from React state.
    setPosition((prev) => {
      dragGrabOffsetRef.current = { dx: x - prev.x, dy: y - prev.y };
      return prev;
    });
    setDragPreview({ x: x - dragGrabOffsetRef.current.dx, y: y - dragGrabOffsetRef.current.dy });
  }, []);

  const updateDrag = useCallback((x: number, y: number) => {
    const { dx, dy } = dragGrabOffsetRef.current;
    setDragPreview({ x: x - dx, y: y - dy });
  }, []);

  const endDrag = useCallback((x: number, y: number) => {
    if (typeof window === "undefined") {
      setDragPreview(null);
      return;
    }
    const { dx, dy } = dragGrabOffsetRef.current;
    const { w, h } = elementSizeRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamped = clampPosition({
      x: x - dx,
      y: y - dy,
      viewportW: vw,
      viewportH: vh,
      elementW: w,
      elementH: h,
    });
    setPosition(clamped);
    writeStored({ v: 1, x: clamped.x, y: clamped.y, vw, vh });
    setDragPreview(null);
  }, []);

  const cancelDrag = useCallback(() => {
    setDragPreview(null);
  }, []);

  const resetPosition = useCallback(() => {
    if (typeof window === "undefined") return;
    clearStored();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { w, h } = elementSizeRef.current;
    setPosition(defaultPosition({ viewportW: vw, viewportH: vh, elementW: w, elementH: h }));
    setDragPreview(null);
  }, []);

  return {
    position,
    dragPreview,
    isDragging: dragPreview !== null,
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    resetPosition,
  };
}
